export type ShareFlowErrorReason =
  | "request_failed"
  | "missing_share_token"
  | "copy_failed";

export type ShareFlowResult =
  | {
      status: "success";
      token: string;
      url: string;
      popupOpened: boolean;
    }
  | {
      status: "error";
      reason: ShareFlowErrorReason;
      error?: unknown;
    };

interface RunShareAnalyticsFlowOptions {
  origin: string;
  requestShareToken: () => Promise<unknown>;
  copyToClipboard: (url: string) => Promise<boolean> | boolean;
  popupWindow?: Window | null;
  existingToken?: string | null;
}

export const PUBLIC_MATCH_SHARE_ROUTE_PREFIX = "/match";
export const PUBLIC_MATCH_SHARE_ROUTE_PATH = `${PUBLIC_MATCH_SHARE_ROUTE_PREFIX}/:shareToken`;
export const LEGACY_PUBLIC_MATCH_SHARE_ROUTE_PATH = "/public/match/:shareToken";

const toNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const extractShareToken = (payload: unknown): string | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const directToken = toNonEmptyString(record.share_token);
  if (directToken) {
    return directToken;
  }

  // Support legacy axios-shaped payloads if they appear in older code paths.
  const nestedData = record.data;
  if (nestedData && typeof nestedData === "object") {
    const nestedToken = toNonEmptyString(
      (nestedData as Record<string, unknown>).share_token,
    );
    if (nestedToken) {
      return nestedToken;
    }
  }

  return null;
};

// Mirrors the redirect map in index.html: a share link must never be minted
// on a Firebase default domain, even from a tab that loaded before the
// app-shell redirect was deployed and never reloaded since.
const CANONICAL_ORIGINS: Record<string, string> = {
  "https://promatchanalytics.web.app": "https://promatchanalytics.com",
  "https://promatchanalytics.firebaseapp.com": "https://promatchanalytics.com",
  "https://promatchanalytics-development.web.app":
    "https://dev.promatchanalytics.com",
  "https://promatchanalytics-development.firebaseapp.com":
    "https://dev.promatchanalytics.com",
};

const canonicalizeAppOrigin = (origin: string): string => {
  const normalizedOrigin = origin.endsWith("/") ? origin.slice(0, -1) : origin;
  return CANONICAL_ORIGINS[normalizedOrigin] ?? normalizedOrigin;
};

export const buildPublicMatchShareUrl = (
  origin: string,
  token: string,
): string => {
  const normalizedOrigin = canonicalizeAppOrigin(origin);
  return `${normalizedOrigin}${PUBLIC_MATCH_SHARE_ROUTE_PREFIX}/${token}`;
};

export const preOpenSharePopup = (): Window | null => {
  if (typeof window === "undefined" || typeof window.open !== "function") {
    return null;
  }

  try {
    return window.open("about:blank", "_blank");
  } catch {
    return null;
  }
};

export const closeSharePopup = (popupWindow?: Window | null): void => {
  if (!popupWindow) {
    return;
  }

  try {
    popupWindow.close();
  } catch {
    // Ignore close errors for blocked or cross-origin popup handles.
  }
};

export const navigateSharePopup = (
  popupWindow: Window | null | undefined,
  url: string,
): boolean => {
  if (!popupWindow || popupWindow.closed) {
    return false;
  }

  try {
    popupWindow.location.href = url;
    if (typeof popupWindow.focus === "function") {
      popupWindow.focus();
    }
    return true;
  } catch {
    closeSharePopup(popupWindow);
    return false;
  }
};

export const runShareAnalyticsFlow = async (
  options: RunShareAnalyticsFlowOptions,
): Promise<ShareFlowResult> => {
  let token = toNonEmptyString(options.existingToken);

  if (!token) {
    let payload: unknown;
    try {
      payload = await options.requestShareToken();
    } catch (error) {
      closeSharePopup(options.popupWindow);
      return {
        status: "error",
        reason: "request_failed",
        error,
      };
    }

    token = extractShareToken(payload);
  }

  if (!token) {
    closeSharePopup(options.popupWindow);
    return {
      status: "error",
      reason: "missing_share_token",
    };
  }

  const url = buildPublicMatchShareUrl(options.origin, token);
  const popupOpened = navigateSharePopup(options.popupWindow, url);

  try {
    await Promise.resolve(options.copyToClipboard(url));
  } catch (error) {
    return {
      status: "error",
      reason: "copy_failed",
      error,
    };
  }

  return {
    status: "success",
    token,
    url,
    popupOpened,
  };
};
